import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  RiVideoLine,
  RiRobotLine,
  RiCheckLine,
  RiArrowRightLine,
  RiLockLine,
  RiArrowLeftLine
} from 'react-icons/ri';
import GlassCard from '../../components/ui/v2/GlassCard';
import GlassButton from '../../components/ui/v2/GlassButton';
import { useInterview } from '../../context/InterviewContext';
import { simulationService } from '../../services/simulationService';
import { cn } from '../../utils/cn';

export default function InterviewMode() {
  const [selectedMode, setSelectedMode] = useState<string>('');
  const { setInterviewMode } = useInterview();
  const navigate = useNavigate();

  // Pre-load simulation data to avoid loading flickers in setup
  useEffect(() => {
    simulationService.getPersonas().catch(() => { });
    simulationService.getStages().catch(() => { });
  }, []);

  const interviewModes = [
    {
      id: 'video',
      title: 'Video Interview',
      description: 'Record specific answers',
      features: [
        'Pre-recorded responses',
        'Review and retake',
        'Professional analysis',
        'Detailed scoring'
      ],
      icon: RiVideoLine,
      available: true
    },
    {
      id: 'ai',
      title: 'AI Simulation',
      description: 'Live conversation',
      features: [
        'Real-time conversation',
        'Voice interaction',
        'Instant feedback',
        'Dynamic questions'
      ],
      icon: RiRobotLine,
      available: true,
      comingSoon: false
    }
  ];

  const handleContinue = () => {
    if (selectedMode) {
      const mode = interviewModes.find(m => m.id === selectedMode);
      if (mode && mode.available) {
        setInterviewMode(selectedMode);
        if (selectedMode === 'video') {
          navigate('/interview-setup');
        } else {
          navigate('/simulation-setup');
        }
      }
    }
  };

  const handleModeSelect = (modeId: string, available: boolean) => {
    if (available) {
      setSelectedMode(modeId);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0B0F] text-white selection:bg-white/20 font-sans overflow-hidden relative">

      {/* Background Ambience */}
      <div className="absolute top-0 left-0 w-full h-[500px] bg-gradient-to-b from-blue-900/10 to-transparent pointer-events-none" />
      <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-purple-500/5 rounded-full blur-3xl pointer-events-none" />

      {/* Header */}
      <header className="relative z-10 p-6 flex items-center justify-between max-w-7xl mx-auto">
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors group"
        >
          <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center group-hover:bg-white/10 transition-colors">
            <RiArrowLeftLine />
          </div>
          <span className="text-sm font-medium">Back to Dashboard</span>
        </button>
      </header>

      <main className="relative z-10 max-w-5xl mx-auto px-6 py-12 flex flex-col items-center">

        {/* Title Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16 space-y-4"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-blue-400 text-xs font-semibold tracking-wider uppercase mb-2">
            Interview Preparation
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white tracking-tight">
            Choose Your Interview Style
          </h1>
          <p className="text-lg text-gray-400 max-w-xl mx-auto">
            Select the format that best fits your goals. Practice with tailored scenarios to master your next interview.
          </p>
        </motion.div>

        {/* Cards Grid */}
        <div className="grid md:grid-cols-2 gap-8 w-full mb-12">
          {interviewModes.map((mode, index) => {
            const isSelected = selectedMode === mode.id;
            const Icon = mode.icon;

            return (
              <motion.div
                key={mode.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 + 0.2 }}
              >
                <GlassCard
                  className={cn(
                    "relative h-full flex flex-col p-8 transition-all duration-300 border",
                    isSelected
                      ? "bg-white/[0.08] border-blue-500/50 shadow-[0_0_30px_rgba(59,130,246,0.15)] scale-[1.02]"
                      : "border-white/5 hover:border-white/20 hover:bg-white/[0.04]",
                    !mode.available && "opacity-50 grayscale cursor-not-allowed hover:border-white/5 hover:bg-transparent"
                  )}
                  onClick={() => handleModeSelect(mode.id, mode.available)}
                >
                  {/* Selection Indicator */}
                  <div className={cn(
                    "absolute top-6 right-6 w-6 h-6 rounded-full border flex items-center justify-center transition-all duration-300",
                    isSelected
                      ? "bg-blue-500 border-blue-500 text-white scale-100"
                      : "border-white/20 text-transparent scale-90"
                  )}>
                    <RiCheckLine size={14} />
                  </div>

                  {/* Icon */}
                  <div className={cn(
                    "w-14 h-14 rounded-2xl flex items-center justify-center mb-6 transition-colors duration-300",
                    isSelected ? "bg-blue-600 text-white shadow-lg shadow-blue-900/50" : "bg-white/5 text-gray-400 group-hover:text-white"
                  )}>
                    <Icon size={28} />
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-white mb-2">{mode.title}</h3>
                    <p className="text-gray-400 mb-8">{mode.description}</p>

                    <ul className="space-y-3">
                      {mode.features.map((feature, i) => (
                        <li key={i} className="flex items-center gap-3 text-sm text-gray-300">
                          <div className={cn(
                            "w-1.5 h-1.5 rounded-full transition-colors",
                            isSelected ? "bg-blue-400" : "bg-gray-600"
                          )} />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Unavailable Badge */}
                  {!mode.available && (
                    <div className="absolute inset-0 bg-[#0B0B0F]/60 backdrop-blur-[1px] flex items-center justify-center rounded-2xl z-10">
                      <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-black/40 border border-white/10 text-gray-400 text-sm font-medium backdrop-blur-md">
                        <RiLockLine />
                        <span>Coming Soon</span>
                      </div>
                    </div>
                  )}
                </GlassCard>
              </motion.div>
            );
          })}
        </div>

        {/* Action Button */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="w-full max-w-sm"
        >
          <GlassButton
            onClick={handleContinue}
            disabled={!selectedMode}
            className="w-full py-4 text-base font-semibold group relative overflow-hidden"
            gradient={!!selectedMode}
          >
            <div className="relative z-10 flex items-center justify-center gap-2">
              Start Interview
              <RiArrowRightLine className="transition-transform group-hover:translate-x-1" />
            </div>
          </GlassButton>
        </motion.div>

      </main>
    </div>
  );
}